/**
 * PremiumInput.jsx
 * Design System Premium - Input Components
 *
 * Uses pure CSS classes to avoid Stylis/Emotion crashes.
 * See: /common/theme/premiumInputs.css
 */

import { forwardRef } from 'react';
import {
 TextField, Select, Autocomplete, FormControl, InputLabel, OutlinedInput 
} from '@mui/material';
import clsx from 'clsx';

// Import pure CSS styles
import '../../theme/premiumInputs.css';

/**
 * PremiumInput - Enhanced TextField with premium styling
 *
 * @param {string} variant - 'default' | 'glass' | 'filled' | 'ghost'
 * @param {string} premiumSize - 'sm' | 'md' | 'lg' (CSS sizing, not MUI size prop)
 * @param {boolean} success - Show success state
 * @param {string} className - Additional CSS classes
 */
const PremiumInput = forwardRef(({
  variant = 'default',
  premiumSize = 'md',
  success = false,
  className,
  ...props
}, ref) => {
  const classes = clsx(
    'premium-input',
    `premium-input-${premiumSize}`,
    {
      'premium-input-glass': variant === 'glass',
      'premium-input-filled': variant === 'filled',
      'premium-input-ghost': variant === 'ghost',
      'premium-input-success': success,
    },
    className
  );

  return (
    <TextField
      ref={ref}
      className={classes}
      fullWidth
      variant="outlined"
      size="small"
      {...props}
    />
  );
});

PremiumInput.displayName = 'PremiumInput';

export default PremiumInput;

/**
 * SearchInput - Input optimized for search fields
 */
export const SearchInput = forwardRef(({ className, ...props }, ref) => (
  <PremiumInput
    ref={ref}
    variant="ghost"
    premiumSize="sm"
    className={clsx('premium-search-input', className)}
    {...props}
  />
));

SearchInput.displayName = 'SearchInput';

/**
 * PasswordInput - Input for password fields
 */
export const PasswordInput = forwardRef(({ className, ...props }, ref) => (
  <PremiumInput
    ref={ref}
    type="password"
    className={clsx('premium-password-input', className)}
    {...props}
  />
));

PasswordInput.displayName = 'PasswordInput';

/**
 * TextArea - Multiline text input
 */
export const TextArea = forwardRef(({ className, rows = 4, ...props }, ref) => (
  <PremiumInput
    ref={ref}
    multiline
    rows={rows}
    className={clsx('premium-textarea', className)}
    {...props}
  />
));

TextArea.displayName = 'TextArea';

/**
 * NumberInput - Input for numeric values
 */
export const NumberInput = forwardRef(({ className, ...props }, ref) => (
  <PremiumInput
    ref={ref}
    type="number"
    className={clsx('premium-number-input', className)}
    {...props}
  />
));

NumberInput.displayName = 'NumberInput';

/**
 * EmailInput - Input for email addresses
 */
export const EmailInput = forwardRef(({ className, ...props }, ref) => (
  <PremiumInput
    ref={ref}
    type="email"
    className={clsx('premium-email-input', className)}
    {...props}
  />
));

EmailInput.displayName = 'EmailInput';

/**
 * PhoneInput - Input for phone numbers
 */
export const PhoneInput = forwardRef(({ className, ...props }, ref) => (
  <PremiumInput
    ref={ref}
    type="tel"
    className={clsx('premium-phone-input', className)}
    {...props}
  />
));

PhoneInput.displayName = 'PhoneInput';

/**
 * PremiumSelect - Enhanced Select with premium styling
 */
export const PremiumSelect = forwardRef(({
  className,
  children,
  label,
  ...props
}, ref) => (
  <FormControl
    ref={ref}
    className={clsx('premium-input', 'premium-select', className)}
    fullWidth
    size="small"
  >
    {label && <InputLabel>{label}</InputLabel>}
    <Select label={label} {...props}>
      {children}
    </Select>
  </FormControl>
));

PremiumSelect.displayName = 'PremiumSelect';

/**
 * PremiumAutocomplete - Enhanced Autocomplete with premium styling
 */
export const PremiumAutocomplete = forwardRef(({
  className,
  ...props
}, ref) => (
  <Autocomplete
    ref={ref}
    className={clsx('premium-input', 'premium-autocomplete', className)}
    size="small"
    {...props}
  />
));

PremiumAutocomplete.displayName = 'PremiumAutocomplete';
